import type {
  UpcomingSession,
  ExtraClassesResponse,
  UnifiedSession,
  ClassObject,
  MasterClassObject,
} from '../types';

/**
 * Converts an UpcomingSession to UnifiedSession format
 */
export function convertUpcomingSession(
  session: UpcomingSession
): UnifiedSession {
  return {
    session_id: session.session_id,
    session_type: session.session_type,
    timestamp: session.timestamp,
    timezone: session.timezone,
    readable_date: session.readable_date,
    readable_time: session.readable_time,
    readable_day: session.readable_day,
    remaining_seconds: session.remaining_seconds,
    subject: session.subject ?? '',
    number: session.number,
    isExtraClass: false,
    originalData: session,
    variant_id: session.variant_id,
  };
}

/**
 * Extracts day name from readable_date (e.g., "Sat, 18 Oct" -> "Saturday")
 */
function extractDayFromDate(readableDate: string | undefined): string {
  if (!readableDate) return '';

  // Parse the date format: "Sat, 18 Oct" or similar
  const [dayAbbreviation] = readableDate.split(',');

  const dayMap: Record<string, string> = {
    Sun: 'Sunday',
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thu: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday',
  };

  return dayMap[dayAbbreviation] ?? dayAbbreviation;
}

function extractDateFromReadableDateTime(
  readableDateTime: string | undefined
): string {
  if (!readableDateTime) return '';

  return readableDateTime.split(',')[1].trim();
}

/**
 * Converts a hobby class to UnifiedSession format
 * Treats the entire class as one session, using the first session's data
 */
function convertHobbyClass(parentClass: ClassObject): UnifiedSession {
  const [firstSession] = parentClass.sessions;

  return {
    session_id: firstSession.session_id,
    session_type: firstSession.session_type as string,
    timestamp: parentClass.timestamp,
    timezone: parentClass.timezone,
    readable_date: extractDateFromReadableDateTime(parentClass.readable_date),
    readable_time: parentClass.readable_time,
    readable_day: extractDayFromDate(parentClass.readable_date),
    remaining_seconds: parentClass.remaining_seconds,
    parent_class_name: parentClass.hobby_class_name,
    isExtraClass: true,
    classType: 'hobby_class',
    originalData: firstSession,
  };
}

/**
 * Converts a master class to UnifiedSession format
 * Treats the entire master class as one session
 */
function convertMasterClass(masterClass: MasterClassObject): UnifiedSession {
  return {
    session_id: masterClass.session_id,
    session_type: 'master_class',
    timestamp: masterClass.timestamp,
    timezone: masterClass.timezone,
    readable_date: extractDateFromReadableDateTime(masterClass.readable_date),
    readable_time: masterClass.readable_time,
    readable_day: extractDayFromDate(masterClass.readable_date),
    remaining_seconds: masterClass.remaining_seconds,
    parent_class_name: masterClass.master_class_name,
    joining_link: masterClass.joining_link,
    isExtraClass: true,
    classType: 'master_class',
    originalData: masterClass,
  };
}

/**
 * Flattens extra classes response into array of UnifiedSession
 * Treats each class (hobby or master) as a single session
 */
export function flattenExtraClasses(
  extraClasses: ExtraClassesResponse | undefined
): UnifiedSession[] {
  if (!extraClasses) return [];

  const sessions: UnifiedSession[] = [];

  // Convert each hobby class to one session
  if (extraClasses.hobby_class?.classes) {
    for (const hobbyClass of extraClasses.hobby_class.classes) {
      if (hobbyClass.sessions.length > 0) {
        sessions.push(convertHobbyClass(hobbyClass));
      }
    }
  }

  // Convert each master class to one session
  if (extraClasses.master_class?.classes) {
    for (const masterClass of extraClasses.master_class.classes) {
      if (masterClass.session_id) {
        sessions.push(convertMasterClass(masterClass));
      }
    }
  }

  return sessions;
}

/**
 * Combines and sorts all sessions chronologically from both APIs
 * Returns array sorted by timestamp (earliest first)
 */
export function getAllSessionsSorted(
  upcomingSessions: UpcomingSession[] | undefined,
  extraClasses: ExtraClassesResponse | undefined
): UnifiedSession[] {
  const allSessions: UnifiedSession[] = [];

  // Add all upcoming sessions
  if (upcomingSessions) {
    for (const session of upcomingSessions) {
      allSessions.push(convertUpcomingSession(session));
    }
  }

  // Add all extra class sessions
  const extraClassSessions = flattenExtraClasses(extraClasses);
  allSessions.push(...extraClassSessions);

  // Filter sessions with valid timestamps
  const validSessions = allSessions.filter(
    session => session.timestamp !== undefined && session.timestamp > 0
  );

  // Sort by timestamp (earliest first)
  // If timestamps are equal, prioritize master class over hobby class
  validSessions.sort((a, b) => {
    const timeDiff = a.timestamp! - b.timestamp!;
    if (timeDiff !== 0) return timeDiff;

    // Same timestamp: master class gets priority
    if (a.classType === 'master_class' && b.classType === 'hobby_class')
      return -1;
    if (a.classType === 'hobby_class' && b.classType === 'master_class')
      return 1;

    return 0;
  });

  return validSessions;
}

/**
 * Gets display name for a session
 * For extra classes: shows "Hobby Class" or "Master Class"
 * For regular sessions: returns empty string
 */
export function getSessionDisplayName(session: UnifiedSession): string {
  if (session.isExtraClass && session.classType) {
    return session.classType === 'master_class'
      ? 'Master Class'
      : 'Hobby Class';
  }
  return '';
}
